import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Dialog } from 'radix-ui'
import { useConfirm } from '@/components/common/ConfirmDialog'
import {
  applyClientSideTableState,
  DataTable,
  useTableState,
  type ColumnDef,
} from '@/components/data-table'
import { BoolIcon } from '@/components/common/BoolIcon'
import { AddButton } from '@/components/common/AddButton'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { useCreateConfiguration, useDeleteConfiguration, useConfigurations } from './hooks'
import type { Configuration } from './types'

const TABLE_ID = 'admin-products'
const CODE_REGEX = /^[A-Z][A-Z0-9_]{0,63}$/

export function ConfigurationsAdminPage() {
  const confirm = useConfirm()
  const products = useConfigurations(false)
  const del = useDeleteConfiguration()
  const [creating, setCreating] = useState(false)
  const { state, setSort, setSearch, setFilter, removeFilter, clearFilters } = useTableState(TABLE_ID)

  const columns: ColumnDef<Configuration>[] = useMemo(
    () => [
      {
        key: 'code',
        header: 'Kod',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj kod...' },
        accessor: (p) => (
          <Link to={`/settings/configurations/${p.code}`} className="font-mono text-sky-700 hover:underline">
            {p.code}
          </Link>
        ),
        className: 'whitespace-nowrap',
        alwaysVisible: true,
      },
      {
        key: 'labelPl',
        header: 'Nazwa',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj nazwę...' },
        accessor: (p) => p.labelPl,
        alwaysVisible: true,
      },
      {
        key: 'isActive',
        header: 'Aktywny',
        sortable: true,
        filter: { kind: 'bool', trueLabel: 'Aktywne', falseLabel: 'Nieaktywne' },
        accessor: (p) => <BoolIcon value={p.isActive} />,
        className: 'w-24',
      },
      {
        key: 'actions',
        header: '',
        accessor: (p) => (
          <div className="text-right whitespace-nowrap">
            <Link
              to={`/settings/configurations/${p.code}`}
              className="text-xs text-sky-700 hover:text-sky-900 px-1"
            >
              Edytuj
            </Link>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation()
                const ok = await confirm({
                  title: `Usunąć konfigurację ${p.code}?`,
                  description: 'Operacja zablokowana jeśli istnieją umowy w tym produkcie.',
                  confirmLabel: 'Usuń',
                  variant: 'destructive',
                })
                if (ok) del.mutate(p.code, { onError: (err) => toast.error(formatError(err)) })
              }}
              className="text-xs text-red-600 px-1 ml-1"
            >
              Usuń
            </button>
          </div>
        ),
        className: 'w-32',
        alwaysVisible: true,
      },
    ],
    [del, confirm],
  )

  const filteredRows = useMemo(
    () =>
      applyClientSideTableState(products.data ?? [], columns, state, {
        valueGetters: {
          code: (p) => p.code,
          labelPl: (p) => p.labelPl,
          isActive: (p) => p.isActive,
        },
        searchString: (p) => `${p.code} ${p.labelPl}`.toLowerCase(),
      }),
    [products.data, columns, state],
  )

  return (
    <div className="flex flex-col w-full">
      <ListPageHeader
        title="Konfiguracje umów"
        subtitle="Każda konfiguracja umowy ma własne sekcje, tory statusów i wymagania przejść."
        actions={<AddButton onClick={() => setCreating(true)}>Dodaj konfigurację</AddButton>}
      />

      <div className="p-4">
        <DataTable<Configuration>
          tableId={TABLE_ID}
          data={filteredRows}
          columns={columns}
          rowKey={(p) => p.code}
          isLoading={products.isLoading}
          isError={products.isError}
          sort={state.sort}
          onSortChange={setSort}
          state={state}
          searchable
          searchPlaceholder="Szukaj kodu / nazwy..."
          onSearchChange={setSearch}
          onFilterChange={setFilter}
          onFilterRemove={removeFilter}
          onClearAll={clearFilters}
          emptyState="Brak konfiguracji."
        />
      </div>

      {creating && <CreateConfigurationDialog onClose={() => setCreating(false)} />}
    </div>
  )
}

function CreateConfigurationDialog({ onClose }: { onClose: () => void }) {
  const create = useCreateConfiguration()
  const [code, setCode] = useState('')
  const [labelPl, setLabelPl] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    setError(null)
    if (!CODE_REGEX.test(code)) {
      setError('Code: WIELKIE_LITERY, cyfry, podkreślenia. Musi zaczynać się od litery.')
      return
    }
    if (!labelPl.trim()) { setError('Nazwa nie może być pusta.'); return }
    create.mutate(
      { code, labelPl, sortOrder: 100, isActive: true },
      { onSuccess: onClose, onError: e => setError(formatError(e)) },
    )
  }

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[460px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">Nowa konfiguracja</Dialog.Title>
          <Dialog.Description className="text-xs text-slate-500 mt-1">
            Po utworzeniu wejdź w Edytuj, żeby podpiąć sekcje, tory statusów i resztę.
          </Dialog.Description>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Kod</span>
              <input
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                disabled={create.isPending}
                className="border rounded px-2 py-1 font-mono"
                placeholder="POMPA_CIEPLA"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Nazwa</span>
              <input value={labelPl} onChange={e => setLabelPl(e.target.value)} disabled={create.isPending} className="border rounded px-2 py-1" />
            </label>

            {error && <p className="text-red-600 text-xs">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={create.isPending}>Anuluj</button>
              <button type="button" onClick={submit} className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50" disabled={create.isPending}>
                {create.isPending ? 'Tworzenie...' : 'Utwórz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd zapisu'
}
